import * as Sentry from '@sentry/react'
import type { AxiosError, InternalAxiosRequestConfig } from 'axios'
import axios from 'axios'

type NavigatorConnection = {
    downlink?: number
    effectiveType?: string
    rtt?: number
}

type ClientNetworkSnapshot = {
    downlink?: number
    effectiveType?: string
    online?: boolean
    rtt?: number
    visibility?: DocumentVisibilityState
}

const omitUndefined = (record: Record<string, unknown>): Record<string, unknown> =>
    Object.fromEntries(Object.entries(record).filter(([, value]) => value !== undefined))

const resolveRequestUrl = (config?: InternalAxiosRequestConfig): string => {
    const url = config?.url

    if (!url) {
        return 'unknown'
    }

    if (!config.baseURL) {
        return url
    }

    try {
        return new URL(url, config.baseURL).href
    } catch {
        return url
    }
}

const resolveHost = (requestUrl: string): string => {
    try {
        return new URL(requestUrl).host || 'unknown'
    } catch {
        return 'unknown'
    }
}

const hasAuthorizationHeader = (config?: InternalAxiosRequestConfig): boolean => {
    const headers = config?.headers

    if (!headers) {
        return false
    }

    if (typeof headers.get === 'function') {
        return Boolean(headers.get('Authorization') || headers.get('authorization'))
    }

    const record = headers as Record<string, unknown>

    return Boolean(record.Authorization || record.authorization)
}

const readXhrState = (request: unknown): { readyState?: number; status?: number } => {
    if (!request || typeof request !== 'object') {
        return {}
    }

    if (typeof XMLHttpRequest !== 'undefined' && request instanceof XMLHttpRequest) {
        return {
            readyState: request.readyState,
            status: request.status,
        }
    }

    const candidate = request as { readyState?: unknown; status?: unknown }

    return {
        readyState: typeof candidate.readyState === 'number' ? candidate.readyState : undefined,
        status: typeof candidate.status === 'number' ? candidate.status : undefined,
    }
}

const readClientNetworkSnapshot = (): ClientNetworkSnapshot => {
    const navigatorRef = globalThis.navigator as
        | (Navigator & { connection?: NavigatorConnection })
        | undefined
    const connection = navigatorRef?.connection
    const online = navigatorRef?.onLine

    return {
        downlink: typeof connection?.downlink === 'number' ? connection.downlink : undefined,
        effectiveType: connection?.effectiveType,
        online: typeof online === 'boolean' ? online : undefined,
        rtt: typeof connection?.rtt === 'number' ? connection.rtt : undefined,
        visibility: globalThis.document?.visibilityState,
    }
}

const isCanceledAxiosError = (error: AxiosError): boolean =>
    axios.isCancel(error) || error.code === 'ERR_CANCELED'

export const addHttpRequestBreadcrumb = (config: InternalAxiosRequestConfig): void => {
    Sentry.addBreadcrumb({
        category: 'http',
        message: `${config.method?.toUpperCase() ?? 'GET'} ${config.url ?? ''}`,
        level: 'info',
        data: {
            url: config.url,
            method: config.method,
        },
    })
}

export const addHttpErrorBreadcrumb = (error: AxiosError): void => {
    Sentry.addBreadcrumb({
        category: 'http',
        message: `${error.config?.method?.toUpperCase() ?? 'GET'} ${error.config?.url ?? ''}`,
        level: 'error',
        data: {
            url: error.config?.url,
            baseURL: error.config?.baseURL,
            method: error.config?.method,
            status: error.response?.status,
            code: error.code,
        },
    })
}

const wrapAxiosErrorForSentry = (
    axiosError: AxiosError,
    snapshot: ClientNetworkSnapshot,
): Error => {
    const method = axiosError.config?.method?.toUpperCase() ?? 'REQUEST'
    const url = resolveRequestUrl(axiosError.config)
    const status = axiosError.response?.status

    if (status !== undefined) {
        return new Error(`${method} ${url} (HTTP ${status}): ${axiosError.message}`, {
            cause: axiosError,
        })
    }

    const diagnostic = [
        axiosError.code ? `[${axiosError.code}]` : '',
        snapshot.online === undefined ? '' : `online=${snapshot.online}`,
        snapshot.visibility ? `visibility=${snapshot.visibility}` : '',
    ]
        .filter(Boolean)
        .join(' ')
    const diagnosticPart = diagnostic ? ` ${diagnostic}` : ''

    return new Error(`${method} ${url}${diagnosticPart}: ${axiosError.message}`, {
        cause: axiosError,
    })
}

export const captureSentryException = (
    error: unknown,
    options?: {
        extra?: Record<string, unknown>
        tags?: Record<string, number | string>
    },
): void => {
    const axiosError = axios.isAxiosError(error) ? error : null

    if (axiosError && isCanceledAxiosError(axiosError)) {
        return
    }

    const snapshot = axiosError ? readClientNetworkSnapshot() : null
    const exceptionToCapture =
        axiosError && snapshot ? wrapAxiosErrorForSentry(axiosError, snapshot) : error

    Sentry.withScope((scope) => {
        if (axiosError && snapshot) {
            const requestUrl = resolveRequestUrl(axiosError.config)
            const xhr = readXhrState(axiosError.request)

            scope.setTag('http_status', String(axiosError.response?.status ?? 'unknown'))
            scope.setTag('request_url', axiosError.config?.url ?? 'unknown')
            scope.setTag('axios_code', axiosError.code ?? 'unknown')
            scope.setTag('host', resolveHost(requestUrl))

            if (snapshot.online !== undefined) {
                scope.setTag('online', String(snapshot.online))
            }

            if (snapshot.visibility) {
                scope.setTag('visibility', snapshot.visibility)
            }

            if (snapshot.effectiveType) {
                scope.setTag('effective_type', snapshot.effectiveType)
            }

            scope.setContext(
                'axios_error',
                omitUndefined({
                    url: axiosError.config?.url,
                    baseURL: axiosError.config?.baseURL,
                    fullUrl: requestUrl,
                    method: axiosError.config?.method,
                    status: axiosError.response?.status,
                    statusText: axiosError.response?.statusText,
                    message: axiosError.message,
                    responseData: axiosError.response?.data,
                    code: axiosError.code,
                    timeout: axiosError.config?.timeout,
                    aborted: axiosError.config?.signal?.aborted,
                    hasAuthorization: hasAuthorizationHeader(axiosError.config),
                    online: snapshot.online,
                    visibility: snapshot.visibility,
                    xhrStatus: xhr.status,
                    xhrReadyState: xhr.readyState,
                    effectiveType: snapshot.effectiveType,
                    rtt: snapshot.rtt,
                    downlink: snapshot.downlink,
                }),
            )
        }

        if (options?.tags) {
            for (const [key, value] of Object.entries(options.tags)) {
                scope.setTag(key, String(value))
            }
        }

        if (options?.extra) {
            scope.setContext('extra', options.extra)
        }

        Sentry.captureException(exceptionToCapture)
    })
}
